"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect } from "react"
import { authService, type LoginRequest, type LoginResponse } from "../services/auth"

interface AuthContextType {
    isAuthenticated: boolean
    accountId: string | null
    login: (email: string, password: string) => Promise<LoginResponse>
    logout: () => void
    getAccessToken: () => string | null
    getRefreshToken: () => string | null
    getAccountId: () => string | null
    getUserEmail: () => string | null
    getUserName: () => string | null
    getUserSurname: () => string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
    const [accountId, setAccountId] = useState<string | null>(null)
    const [userEmail, setUserEmail] = useState<string | null>(null)
    const [userName, setUserName] = useState<string | null>(null)
    const [userSurname, setUserSurname] = useState<string | null>(null)

    useEffect(() => {
        const checkAuth = () => {
            const accessToken = localStorage.getItem("accessToken")
            const storedAccountId = localStorage.getItem("accountId")

            setIsAuthenticated(!!accessToken)
            setAccountId(storedAccountId)
            setUserEmail(localStorage.getItem("userEmail"))
            setUserName(localStorage.getItem("userName"))
            setUserSurname(localStorage.getItem("userSurname"))

        }

        checkAuth()
        window.addEventListener("storage", checkAuth)

        return () => {
            window.removeEventListener("storage", checkAuth)
        }
    }, [])

    const login = async (email: string, password: string): Promise<LoginResponse> => {
        const loginRequest: LoginRequest = { email, password }
        const response = await authService.login(loginRequest)

        if (response.success && response.content) {
            localStorage.setItem("accessToken", response.content.accessToken)
            localStorage.setItem("refreshToken", response.content.refreshToken)
            localStorage.setItem("userEmail", response.content.userEmail)
            localStorage.setItem("userName", response.content.userName)
            localStorage.setItem("userSurname", response.content.userSurname)

            // Guardar el accountId si está disponible en la respuesta
            if (response.content.accountId) {
                localStorage.setItem("accountId", response.content.accountId)
                setAccountId(response.content.accountId)
            }

            setIsAuthenticated(true)
        }
        return response
    }

    const logout = () => {
        localStorage.removeItem("accessToken")
        localStorage.removeItem("refreshToken")
        localStorage.removeItem("accountId")
        localStorage.removeItem("userEmail")
        localStorage.removeItem("userName")
        localStorage.removeItem("userSurname")
        setIsAuthenticated(false)
        setAccountId(null)
    }

    const getAccessToken = () => localStorage.getItem("accessToken")
    const getRefreshToken = () => localStorage.getItem("refreshToken")
    const getAccountId = () => localStorage.getItem("accountId")
    const getUserEmail = () => localStorage.getItem("userEmail")
    const getUserName = () => localStorage.getItem("userName")
    const getUserSurname = () => localStorage.getItem("userSurname")

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                accountId,
                login,
                logout,
                getAccessToken,
                getRefreshToken,
                getAccountId,
                getUserEmail,
                getUserName,
                getUserSurname,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthContextProvider")
    }
    return context
}

